import React, { useContext, useEffect, useState } from "react";
import PageWrapper from "../../components/PageWrapper";
import useLanguage from "../../utils/useLanguage";
import { useTranslation } from "react-i18next";
import CustomButton from "../../components/CustomButton";
import addIcon from "../../assets/add_icon.png";
import searchIcon from "../../assets/search_icon.png";
import previousIcon from "../../assets/previous_icon.png";
import nextIcon from "../../assets/next_icon.png";
import saveIcon from "../../assets/save_icon.png";
import closeIcon from "../../assets/close_icon.png";
import deleteIcon from "../../assets/delete_Icon.png";
import Modal from "../../components/Modal";
import CustomInput from "../../components/CustomInput";
import axios from "axios";
import Loader from "../../components/Loader";
import ErrorBox from "../../components/ErrorBox";
import PaperDataEntryModal from "./PaperDataEntryModal";
import BackToDashboard from "../../components/BackToDashboard";
import { UserAuth } from "../../utils/AuthContext";
import AreYouSureModal from "../../components/AreYouSureModal";
import PageHeadline from "../../components/PageHeadline";
import PaperTable from "./PaperTable";
import CustomInputWithUnit from "../../components/CustomInputWithUnit";
import axiosClient from "../../services/axiosClient";

const UniformPage = () => {
  const { currentDirection } = useLanguage();
  const { t } = useTranslation("general");
  const { userData } = useContext(UserAuth);

  // STATES
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [loadingModal, setLoadingModal] = useState(false);
  const [loadingRulers, setLoadingRuler] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
  });

  const [collapsedState, setCollapsedState] = useState({});

  const [dimensionSearch, setDimensionSearch] = useState("");
  const [paperThicknessSearch, setPaperThicknessSearch] = useState("");

  const [showDataEntryModal, setShowDataEntryModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentRulerId, setCurrentRulerId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentGroupCode, setCurrentGroupCode] = useState("");
  const [currentGroupDesign, setCurrentGroupDesign] = useState("");
  const [colorInputs, setColorInputs] = useState([
    {
      colorCode: "",
      colorName: "",
      availableStock: 0,
      availableRollsInStock: 0,
      purchasePrice: 0,
    },
  ]);

  // Error handling
  const [errorFields, setErrorFields] = useState({});
  const [formError, setFormError] = useState("");

  const [fabricData, setFabricData] = useState([]);

  /* VALIDATION FUNCTION */
  const validateFields = () => {
    let errors = {};
    let hasError = false;

    // Validate Ruler Code
    if (!currentGroupCode.trim()) {
      errors["code"] = t("rulercodeRequired");
      hasError = true;
    }

    const validColors = colorInputs.filter(
      (c) => c.size?.trim() && c.quantity?.trim(),
    );

    if (validColors.length === 0) {
      errors["colors"] = t("atLeastOneColorRequired");
      hasError = true;
    } else {
      // Check each color for validity
      colorInputs.forEach((color, index) => {
        // Only validate if either field has content to avoid errors on empty rows
        if (color.size?.trim() || color.quantity) {
          if (!color.size?.trim()) {
            errors[`size-${index}`] = t("colorCodeRequired");
            hasError = true;
          }
          if (!color.quantity?.trim()) {
            errors[`quantity-${index}`] = t("colorNameRequired");
            hasError = true;
          }
        }
      });
    }

    setErrorFields(errors);
    return !hasError;
  };

  /* ENDPOINTS FUNCTIONS */

  // creating a ruler with it's colors
  const handleAddGroup = async () => {
    if (!validateFields()) {
      setFormError(t("pleaseEnterValidValues"));
      return;
    }

    const organizationId = localStorage.getItem("organizationId");
    if (!organizationId || !currentGroupCode) return;

    const validSizes = colorInputs.filter(
      (c) => c.size?.trim() && c.quantity !== null,
    );

    if (validSizes.length === 0) return;

    const payload = {
      id: Math.random().toString(),
      name: currentGroupCode,
      color: currentGroupDesign,
      organizationId: organizationId,
      items: validSizes.map((color) => {
        return {
          notes: color.notes,
          quantity: parseInt(color.quantity),
          size: color.size,
          purchasePrice: parseInt(color.purchasePrice),
          sellPrice: parseInt(color.sellPrice),
        };
      }),
    };

    try {
      setLoadingModal(true);
      setFormError("");

      const response = await axiosClient.post("productRuler", payload, {
        headers: {
          organizationid: organizationId,
        },
      });

      if (response.status === 201) {
        resetForm();
        setShowDataEntryModal(false);
        getAllRulers();
      }

      setLoadingModal(false);
    } catch (err) {
      console.error("Network error:", err);
      if (err.response.data.error === "productRulerAlreadyExist") {
        setFormError(t("UniformAlreadyExist"));
        return;
      }
      setFormError(t("errorAddingNewUniform"));
    } finally {
      setLoadingModal(false);
    }
  };

  // update a ruler with its colors
  const handleUpdateGroup = async () => {
    if (colorInputs.length === 0) {
      setFormError(t("shouldAddAtLeastOneSize"));
      return;
    }
    if (!validateFields()) {
      setFormError(t("emptyFields"));
      return;
    }

    const organizationId = localStorage.getItem("organizationId");
    if (!organizationId || !currentGroupCode || !currentRulerId) return;

    const currentRuler = fabricData.find(
      (ruler) => ruler.id === currentRulerId,
    );

    const validSizesInputs = colorInputs.filter(
      (c) => c.size?.trim() && c.quantity !== null,
    );

    const payload = {
      name: currentGroupCode,
      color: currentGroupDesign,
      organizationId: organizationId,
      organizationId: parseInt(organizationId),
      items: validSizesInputs.map((color) => {
        return {
          id: color.id,
          notes: color.notes,
          quantity: parseInt(color.quantity),
          size: color.size,
          purchasePrice: parseInt(color.purchasePrice),
          sellPrice: parseInt(color.sellPrice),
        };
      }),
    };

    try {
      setLoadingModal(true);
      setFormError("");

      const response = await axiosClient.put(
        `productRuler/${currentRulerId}`,
        payload,
        {
          headers: {
            organizationid: organizationId,
          },
        },
      );

      if (response.status === 200) {
        resetForm();
        setShowDataEntryModal(false);
        getAllRulers();
      }
    } catch (err) {
      console.error("Network error:", err);
      setFormError(t("errorUpdatingUniform"));
    } finally {
      setLoadingModal(false);
    }
  };

  // get all rulers
  const getAllRulers = async () => {
    const organizationId = localStorage.getItem("organizationId");
    if (!organizationId) return;

    try {
      setLoadingRuler(true);
      const response = await axiosClient.get("/productRuler", {
        params: {
          organizationId,
          page: pagination.currentPage,
          limit: 5,
          color: dimensionSearch.color,
          size: dimensionSearch.size,
          search: searchQuery,
        },
      });

      if (response.status === 200 && response.data) {
        setFabricData(
          response.data.productRulers?.map((item) => ({
            id: item.id,
            name: item.name,
            color: item.color,
            sizes: item.productRulerItem?.map((item) => ({
              id: item.id,
              size: item.size,
              quantity: item.quantity.toString(),
              purchasePrice: item.purchasePrice,
              sellPrice: item.sellPrice,
              notes: item.notes,
            })),
          })),
        );
        setPagination({
          currentPage: response.data.pagination.page,
          totalPages: response.data.pagination.totalPages,
          totalRecords: response.data.pagination.total,
        });
      }
    } catch (err) {
      console.error("Failed to fetch rulers:", err);
    } finally {
      setLoadingRuler(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!currentRulerId) return;

    try {
      setLoadingModal(true);
      setFormError("");

      const response = await axiosClient.delete(
        `productRuler/${currentRulerId}`,
      );

      if (response.status === 200) {
        setFabricData((prev) =>
          prev.filter((ruler) => ruler.id !== currentRulerId),
        );

        resetForm();
        setShowDataEntryModal(false);
      }
    } catch (err) {
      console.error("Error deleting ruler:", err);
      setFormError(err.response?.data?.error || t("errorDeletingRuler"));
    } finally {
      setLoadingModal(false);
      setShowDeleteConfirmation(false);
    }
  };

  // HANDLER FUNCTIONS
  const handleShowModal = (isEdit = false) => {
    if (!isEdit) {
      resetForm();
      setColorInputs([
        {
          colorCode: "",
          colorName: "",
          availableStock: 0,
          availableRollsInStock: 0,
          purchasePrice: 0,
          createdAt: new Date(), // Set timestamp when modal opens
        },
      ]);
    }
    setIsEditMode(isEdit);
    setShowDataEntryModal(!showDataEntryModal);
    setFormError("");
    setErrorFields({});
  };

  const handleEditRuler = (ruler) => {
    setCurrentRulerId(ruler.id);
    setCurrentGroupCode(ruler.name);
    setCurrentGroupDesign(ruler.color);
    setColorInputs(
      ruler.sizes.map((color) => ({
        id: color.id,
        size: color.size,
        purchasePrice: color.purchasePrice,
        sellPrice: color.sellPrice,
        quantity: color.quantity,
        notes: color.notes,
      })),
    );
    handleShowModal(true);
  };

  const resetForm = () => {
    setCurrentGroupCode("");
    setCurrentGroupDesign("");
    setColorInputs([
      {
        colorCode: "",
        colorName: "",
        availableStock: 0,
        availableRollsInStock: 0,
        purchasePrice: 0,
      },
    ]);
    setCurrentRulerId(null);
    setIsEditMode(false);
    setErrorFields({});
    setFormError("");
  };

  const handleFormSubmit = () => {
    if (isEditMode) {
      handleUpdateGroup();
    } else {
      handleAddGroup();
    }
  };

  const handleDeleteRuler = async () => {
    setShowDeleteConfirmation(true);
  };

  /* SIDE EFFECTS */
  useEffect(() => {
    getAllRulers();
  }, [searchQuery, pagination.currentPage]);

  const handleDeleteSize = async (sizeId) => {
    if (colorInputs.length === 1) {
      setFormError(t("shouldAddAtLeastOneSize"));
      return;
    }
    const prevSizes = fabricData.find(
      (ruler) => ruler.id === currentRulerId,
    )?.sizes;
    const prevColors = colorInputs;

    setFabricData((prev) =>
      prev.map((item) => {
        if (item.id === currentRulerId) {
          console.log("item", item);
          const newSizes = item.sizes.filter((size) => size.id !== sizeId);
          console.log("newSizes", newSizes);
          return {
            ...item,
            sizes: newSizes,
          };
        }
        return item;
      }),
    );

    setColorInputs((prev) => prev.filter((item) => item.id !== sizeId));
    try {
      const response = await axiosClient.delete(`productRulerItem/${sizeId}`);
      if (response.status === 200) {
        setColorInputs((prev) => prev.filter((item) => item.id !== sizeId));
      }
    } catch (error) {
      setColorInputs(prevColors);
      setFabricData(prevSizes);
      console.error("Error deleting size:", error);
    }
  };

  return (
    <PageWrapper title={t("uniforms")} withBackButton>
      <div className="App o-column o-column--gap-size_400">
        <div className="o-row o-row--horizontal-align_end">
          <CustomButton
            variant={"blank"}
            icon={addIcon}
            iconSize={16}
            title={t("addNewUniform")}
            onClick={() => handleShowModal(false)}
          />
        </div>
        <section className="o-row o-row--gap-size_300 multiple-search-filter o-column-row-switcher u-width_full-width">
          <div className="o-column o-column--gap-size_300 multiple-search-filter-dates ">
            <div className="u-width_full-width">
              <label className="t-bold_semi-bold">
                {t("searchByUniformName")}:{" "}
              </label>
              <CustomInput
                placeholder={t("search")}
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                }}
                resetSearch={() => {
                  setSearchQuery("");
                }}
                thick
              />
            </div>
            <div className="o-row o-row--gap-size_400 o-row--vertical-align_end o-column-row-switcher">
              <div className="o-row o-row--gap-size_200">
                <div>
                  <label className="t-bold_semi-bold">{t("size")}: </label>
                  <CustomInput
                    placeholder={t("search")}
                    value={dimensionSearch}
                    onChange={(e) => {
                      setDimensionSearch(e.target.value);
                    }}
                    resetSearch={() => {
                      setDimensionSearch("");
                    }}
                    thick
                  />
                </div>

                <div>
                  <label className="t-bold_semi-bold">{t("color")}:</label>
                  <CustomInput
                    placeholder={t("search")}
                    value={paperThicknessSearch}
                    onChange={(e) => {
                      setPaperThicknessSearch(e.target.value);
                    }}
                    resetSearch={() => {
                      setPaperThicknessSearch("");
                    }}
                    thick
                  />
                </div>
              </div>
              <CustomButton
                title={t("search")}
                iconSize={15}
                classList="c-search-button"
                icon={searchIcon}
                onClick={() => getAllRulers()}
              />
            </div>
          </div>
        </section>

        {loadingRulers ? (
          <Loader />
        ) : (
          <PaperTable
            data={fabricData}
            onEditRuler={handleEditRuler}
            collapsed={collapsedState}
            setCollapsed={setCollapsedState}
          />
        )}
      </div>

      {showDataEntryModal && (
        <Modal
          title={isEditMode ? t("editUniform") : t("addNewUniform")}
          onClose={() => handleShowModal(isEditMode)}
          error={formError}
          footer={
            <>
              {loadingModal ? <Loader /> : null}
              <section className="edit-user-actions">
                <CustomButton
                  title={t("confirm")}
                  onClick={handleFormSubmit}
                  icon={saveIcon}
                  iconSize={16}
                  disabled={loadingModal}
                />
                {isEditMode && (
                  <CustomButton
                    title={t("delete")}
                    onClick={handleDeleteRuler}
                    variant="red"
                    iconSize={16}
                    icon={deleteIcon}
                  />
                )}
                <CustomButton
                  title={t("cancel")}
                  onClick={() => handleShowModal(isEditMode)}
                  variant="blank"
                  icon={closeIcon}
                  iconSize={14}
                />
              </section>
            </>
          }
        >
          {formError && <ErrorBox>{t(formError)}</ErrorBox>}
          <PaperDataEntryModal
            currentGroupCode={currentGroupCode}
            setCurrentGroupCode={setCurrentGroupCode}
            colorInputs={colorInputs}
            setCurrentGroupDesign={setCurrentGroupDesign}
            currentGroupDesign={currentGroupDesign}
            setColorInputs={setColorInputs}
            isEditMode={isEditMode}
            errorFields={errorFields}
            onSizeDelete={handleDeleteSize}
          />
        </Modal>
      )}

      {pagination && (
        <section className="pagination">
          <div className="pagination-buttons">
            <CustomButton
              variant="blank"
              title={t("previous")}
              onClick={() =>
                setPagination((prev) => ({
                  ...prev,
                  currentPage: prev.currentPage - 1,
                }))
              }
              disabled={pagination.currentPage === 1}
              icon={currentDirection === "ltr" ? previousIcon : nextIcon}
              iconSize={15}
            />
            <span className="pagination-location">
              {t("page")}: {pagination.currentPage} - {pagination.totalPages}
            </span>
            <CustomButton
              title={t("next")}
              variant="blank"
              onClick={() =>
                setPagination((prev) => ({
                  ...prev,
                  currentPage: prev.currentPage + 1,
                }))
              }
              disabled={pagination.currentPage === pagination.totalPages}
              icon={currentDirection === "ltr" ? nextIcon : previousIcon}
              iconSize={15}
              reverseIcon
            />
          </div>
          <div className="pagination-buttons">
            <span className="pagination-location">
              {t("totalRecords")}: {pagination.totalRecords}
            </span>
          </div>
        </section>
      )}

      {showDeleteConfirmation && (
        <AreYouSureModal
          onClose={() => setShowDeleteConfirmation(false)}
          onConfirm={handleConfirmDelete}
          customMessage={t("deleteRuler")}
          closeTitle="cancel"
          confirmTitle="delete"
          loading={loadingModal}
          confirmButtonVariant={"red"}
          />
      )}
    </PageWrapper>
  );
};

export default UniformPage;
